/**
 * NP-408 — flag-aware tab visibility.
 *
 * Wraps the pure lifecycle `visibleTabs(state)` and strips tabs whose
 * feature flag is off. For v1.0 the Income Verification and Conditions tabs
 * are hidden (NOT deleted) — flip `IncomeVerificationPage` / `ConditionsPage`
 * (coded default, server flag, VITE_FF_* env, or local config) to restore
 * them. Every tab consumer (Header, MobileBottomNav, RequireTab) must use
 * this hook rather than calling visibleTabs directly.
 */

import { useMemo } from 'react';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useLoan } from '@/context/LoanContext';
import { visibleTabs, type LifecycleState, type TabKey } from '@/lib/lifecycle';

export function useVisibleTabs(state: LifecycleState): TabKey[] {
  const incomeTabEnabled = useFeatureFlag('IncomeVerificationPage');
  const conditionsTabEnabled = useFeatureFlag('ConditionsPage');
  // NP-553: My Home shows placeholder figures, so it is hidden the same way.
  // Filtering here also closes the deep link for free — RequireTab reads this
  // hook, and MyHome already wraps itself in <RequireTab tab="myhome">.
  const myHomeTabEnabled = useFeatureFlag('MyHomePage');
  // NP-754: Payments is incomplete; hidden from the nav the same way.
  const paymentsTabEnabled = useFeatureFlag('PaymentsPage');
  // Tasks (ported from AFN.BorrowerPortal.Web) has no real afn-path-api endpoint
  // yet — see the flag's own comment in lib/featureFlags.ts.
  const tasksTabEnabled = useFeatureFlag('TasksPage');
  // NP-798: an Encompass loan's tasks are LO-facing underwriting conditions the borrower
  // cannot act on, so the tab goes for those loans. Supersedes NP-290's read-only Tasks
  // view, whose code stays for re-enablement. `isReadOnly` is the selected loan's losType.
  const { isReadOnly } = useLoan();

  return useMemo(
    () =>
      visibleTabs(state).filter((tab) => {
        if (tab === 'income') return incomeTabEnabled;
        if (tab === 'conditions') return conditionsTabEnabled;
        if (tab === 'myhome') return myHomeTabEnabled;
        if (tab === 'payments') return paymentsTabEnabled;
        if (tab === 'tasks') return tasksTabEnabled && !isReadOnly;
        return true;
      }),
    [state, incomeTabEnabled, conditionsTabEnabled, myHomeTabEnabled, paymentsTabEnabled, tasksTabEnabled, isReadOnly],
  );
}
